enum MortgageStatus {
  INVALID_STATUS
  STATUS_REPAID
  STATUS_CURRENT
  STATUS_PROPOSED
  STATUS_SELECTED
  STATUS_LENDER_PROPOSED
  STATUS_CREDIT_REPORT_CURRENT
  STATUS_CREDIT_REPORT_SETTLED
}

enum AdditionalLoanPurposes {
  PURPOSE_DEBT_CONSOLIDATION
  PURPOSE_BUSINESS
  PURPOSE_OTHER_PROPERTY
  HOME_IMPROVEMENTS
  LIFESTYLE
  OTHER
}

enum ProductMatchStatus {
  AVAILABLE
  NEAR_MISS
}

enum TermUnit {
  INVALID_TERM_UNIT
  TERM_DAYS
  TERM_MONTHS
  TERM_YEARS
}

enum MortgageProductDetailsMortgageType {
  INVALID_MORTGAGE_TYPE
  RESIDENTIAL
  BTL
  REGULATED_BTL
}

enum MortgageClass {
  CLASS_FIXED
  CLASS_DISCOUNT
  CLASS_VARIABLE
  CLASS_CAPPED
  CLASS_LIBOR
  CLASS_TRACKER
  CLASS_STEPPED
}

enum RateTracker {
  BOE_BASE_RATE
  LENDER_SVR
  LENDER_SVR_2
  LENDER_SVR_3
  LENDER_SVR_4
  LENDER_SVR_5
  LENDER_SVR_6
  LENDER_SVR_7
  LIBOR
  LIBOR_1M
  LIBOR_3M
  LIBOR_6M
  LIBOR_1YR
  SONIA
  SONIA_1M
  SONIA_3M
}

enum CanBeAddedToLoan {
  INVALID_CAN_BE_ADDED_TO_LOAN
  MUST_BE_ADDED
  OPTION_TO_BE_ADDED
  PAY_SEPARATELY
}

enum MortgageProductFeeType {
  INVALID_FEE_TYPE
  TITLE_DEEDS_ADMIN_FEE
  LEGAL_FEE
  VALUATION_FEE
  HIGHER_LENDING_CHARGE
  LENDER_CONVEYANCING_FEE
  BOOKING_FEE
  ARRANGEMENT_FEE
  APPLICATION_FEE
  COMPLETION_FEE
  ADMINISTRATION_FEE
  INSURANCE_ADMINISTRATION
  LAND_REGISTRY_FEE
  MORTGAGE_ACCOUNT_FEE
  MASTER_BROKER_FEE
  PACKAGER_FEE
  PRODUCT_TRANSFER_FEE
  RE_INSPECTION_FEE
  CHAPS_TELEGRAPHIC_TRANSFER_FEE
  REDEMPTION_ADMIN_FEE
  BROKER_FEE
  EARLY_REPAYMENT_CHARGE
  DEEDS_RELEASE_FEE
  ASSESSMENT_FEE
  OTHER_FEE
  CLAWBACK_FEE
  # 3 deprecated fields, that are needed for support of fees_to_be_added, which on old cases can still return these enums
  CHAPS_FEE
  HOMEBUYERS_FEE
  DISBURSEMENT_FEE
}

enum MortgageProductFeeTiming {
  INVALID_TIMING
  REDEMPTION
  EARLY_REDEMPTION
  COMPLETION_TIMING
  UP_FRONT
  NOT_SPECIFIED
}

enum CapitalRepaymentType {
  AMOUNT
  PERCENTAGE
}

enum RateType {
  FIXED
  DISCOUNT
  TRACKER
  INVALID_TYPE
}

enum RepaymentTypes {
  REPAYMENT
  INTEREST_ONLY
  PART_AND_PART
}

enum MortgageDocumentRequirementSource {
  INVALID_SOURCE
  LENDER_VIA_DIP
  LENDER_VIA_MARKETPLACE
  USER_VIA_MESSAGE
  USER
}

type AdditionalLoan {
  amount: String
  purposes: AdditionalLoanPurposes
  reason_description: String
}

type ProcFee {
  code: String
  fixed: String
  percentage: Float
  min: String
  max: String
  value: Int
}

type MortgagePackager {
  name: String
  phone_number: String
  url: String
}

type MortgageStressTestPayment {
  rate_increment_percent: Float
  initial_monthly_payment: Float
  monthly_payment_after_initial_period: Float
}

input MortgageStressTestPaymentInput {
  rate_increment_percent: Float
  initial_monthly_payment: Float
  monthly_payment_after_initial_period: Float
}

type MortgageClubFee {
  mortgage_club_code: String
  mortgage_club_name: String
  proc_fee_code: String
  net_proc_fee_percentage: Float
  proc_fee_percentage: Float
}

type MortgageClubFeeCalculated {
  mortgage_club_code: String
  mortgage_club_name: String
  proc_fee: Float
  net_proc_fee: Float
}

input MortgageClubFeeInput {
  mortgage_club_code: String
  mortgage_club_name: String
  proc_fee_code: String
  net_proc_fee_percentage: Float
  proc_fee_percentage: Float
  proc_fee_fixed: String
  net_proc_fee_fixed: String
}

type ErcPeriodType {
  erc_charge: Int
  period_fixed_length_months: Int
  period_fixed_end_date: String
}

type RateTiersType {
  rate: Int
  rate_type: MortgageClass
  period_fixed_length_months: Int
  period_fixed_end_date: String
  rate_tracked: RateTracker
  discount_rate: Int
  until_end_of_term: Boolean
}

input ErcPeriodInput {
  erc_charge: Int
  period_fixed_length_months: Int
  period_fixed_end_date: String
}

input MortgageEntityVersionInput {
  mortgage_id: String!
  version: Int!
}

type MortgageEntityVersion {
  mortgage_id: String!
  version: Int!
}
type MortgageProduct {
  id: ID
  product_id: String
  product_code: String
  product_name: String
  lender_code: String
  lender_name: String
  lender_reference: String
  initial_pay_rate: Int
  mortgage_class: MortgageClass
  applicant_type_conditions: String
  full_rate_description: String
  apr_lenders: Int
  max_ltv_available: Int
  selected_mortgage_club_code: String
  applicant1_income_multiplier: Float
  applicant2_income_multiplier: Float
  joint_applicant_income_multiplier: Float
  standard_variable_rate: Int
  initial_monthly_payment: Float
  cashback: String
  cashback_conditions: String
  valuation_fee: String
  valuation_fee_conditions: String
  arrangement_fee: String
  arrangement_fee_conditions: String
  booking_fee: String
  booking_fee_conditions: String
  higher_lending_charge: String
  higher_lending_charge_conditions: String
  free_legal_fees: Boolean
  free_legal_fee_conditions: String
  chaps_fee: String
  fees_payable_on_application: String
  fees_payable_on_completion: String
  fees_total: String
  early_repayment_charge_applies_until: String
  early_repayment_charge_applies_until_calculated_date: String
  product_notes: String
  employed_prob_period: String
  max_property_valuation: String
  studio_flat_flag: String
  proc_fee_code: String
  proc_fee_fixed: String
  proc_fee_percentage: Int
  proc_fee_min: String
  proc_fee_max: String
  monthly_payment_after_initial_period_plus_1perc: Float
  monthly_payment_after_initial_period_plus_2perc: Float
  monthly_payment_after_initial_period_plus_3perc: Float
  self_build: String
  shared_ownership: String
  mortgage_discharge_fee: String
  deeds_release_fee: String
  funds_transfer_fee_rule: String
  capital_rest_period: String
  help_to_buy_shared_equity: Boolean
  help_to_buy_mortgage_guarantee: Boolean
  broker_fee: Float
  broker_fee_conditions: String
  packager_name: String
  packager_tel_no: String
  packager_web_address: String
  lender_criteria_url: String
  lender_product_reference: String
  homebuyers_fee: String
  homebuyers_fee_conditions: String
  legal_fee: String
  legal_fee_conditions: String
  product_type: String
  net_proc_fee: Float
  net_proc_fee_fixed: String
  net_proc_fee_percentage: Int
  net_proc_fee_min: String
  net_proc_fee_max: String
  mortgage_discharge_fee_when_to_pay: String
  product_match_status: String
  reject_reasons: String
  max_loan_available: String
  buy_to_let_rental_income_calculation_rate: Float
  stress_test_payments: [MortgageStressTestPayment!]
  disbursement_fee: String
  disbursement_fee_conditions: String
  true_cost_full_term: String
  initial_monthly_payment_inc_existing_mortgage: Float
  true_cost_full_term_inc_existing_mortgage: Float
  true_cost_initial_period_inc_existing_mortgage: Float
  true_cost_inc_existing_mortgage: Float
  mortgage_club_proc_fees: [MortgageClubFee!]
  max_overpayment_percent_no_erc: Float
  overpayments_allowed: Boolean
  underpayments_allowed: Boolean
  payment_holidays_allowed: Boolean
  date_last_modified: String
  optional_partial_repayment: Boolean
  apply_online: Boolean
  lender_tel_no: String
  lender_web_address: String
  initial_rate_period: String
  initial_rate_period_months: Float
  repayment_acceptable: Boolean
  product_availability: String
  available_to_purchase: Boolean
  available_to_remortgage: Boolean
  available_to_ftb: Boolean
  max_loan: String
  min_loan: String
  min_time_in_current_position_months: Float
  min_time_in_continuous_service_months: Float
  min_self_employed_accounts_years: Float
  early_repayment_charge_applies: Boolean
  early_repayment_charge: String
  ccj_maximum_number: Float
  ccj_maximum_amount: Float
  arrears_not_in_last_months: Float
  true_cost: String
  true_cost_over_calculated_period: String
  true_cost_over_initial_period: String
  cashback_paid_on: String
  remort_home_improv_max_ltv: Float
  remort_debt_con_max_ltv: Float
  remort_bus_purpose_max_ltv: Float
  remort_hol_cars_max_ltv: Float
  affordability_calculator: String
  self_years_avg_income: Float
  flat_over_shop_flag: String
  flat_over_shop_notes: String
  flat_over_shop_owner: String
  flat_over_shop_sep_ent: String
  flat_over_shop_sep_title: String
  flat_over_shop_restaurant: String
  flat_over_shop_retail: String
  flat_over_shop_fast_food: String
  flat_over_shop_fast_industry_unit: String
  flat_max_floors: Float
  flat_max_floors_no_lift: Float
  deeds_release_fee_when_to_pay: String
  applicant1_minimum_age: Float
  applicant2_minimum_age: Float
  applicant_maximum_age: Float
  freehold_flag: String
  leasehold_flag: String
  commonhold_flag: String
  feudal_flag: String
  flying_freehold_flag: String
  flying_freehold_max_percentage: Int
  product_available_date: String
  product_discontinued_date: String
  is_offset_product: Boolean
  is_further_advance: Boolean
  is_porting: Boolean
  mortgage_type: MortgageProductDetailsMortgageType
  early_repayment_charge_periods: [ErcPeriodType!]
  exclude_retention: Boolean
  rate_tiers: [RateTiersType!]
  fees: [MortgageProductFee!]
  incentives: [MortgageProductIncentive!]
}

type MortgageProductFee {
  min_amount: String
  max_amount: String
  percentage: Int
  amount: String
  can_be_added_to_loan: CanBeAddedToLoan
  timing: MortgageProductFeeTiming
  refundable: Boolean
  fee_type: MortgageProductFeeType
  other_type: String
}

type MortgageProductIncentive {
  id: ID
  amount: String
  type: String
  maxAmount: String
  method: String
}

type SavedMortgageProduct {
  id: String
  status: String
  case_id: String
  mortgage_product: MortgageProduct
}

type MortgageCalculatedValues {
  cost_over_initial_term_inc_fees_cashback: String
  cost_over_full_term_inc_fees_cashback: String
  fees_at_origination: String
  cost_over_initial_term_inc_fees: String
  cost_over_full_term_inc_fees: String
  cost_over_initial_term_exc_fees: String
  cost_over_full_term_exc_fees: String
  total_mortgage_fees: String
  interest_cost_of_added_fees_full_term: String
  loan_total_fees_added_to_loan: String
  loan_total_premiums_or_insurance_added_to_loan: String
  mortgage_origination_inc_added_fees: String
  monthly_payment: String
  revert_monthly_payment: String
  calculated_net_proc_fee: String
  calculated_gross_proc_fee: String
  calculated_club_proc_fees: [MortgageClubFeeCalculated!]
  fees: [MortgageProductFee!]
  remaining_term_months: String
  can_generate_esis: Boolean
}

type ExternalMortgageSource {
  id: String
  name: ExternalIdNames
}

type ExternalMortgage {
  id: String
  source: ExternalMortgageSource
}

type Mortgage {
  id: ID!
  mortgage_id: String!
  assumes_continuing: [String!]
  property_secured_ids: [String!]
  property: Property
  mortgage_product: MortgageProduct
  mortgage_productCp: MortgageProduct
  mortgage_product_id: String
  clients: [Client]
  case_ids: [String]
  client_ids: [String]
  case_id: String
  status: MortgageStatus
  outstanding_balance: String
  outstanding_balance_date: String
  current_monthly_payment: String
  early_repayment_charge_amount: String
  mortgage_end_date: String
  loan_benefits_all_applicants: Boolean
  additional_loan: AdditionalLoan
  mortgage_account_number: String
  mortgage_start_date: String
  mortgage_redemption_date: String
  property_purchase_date: String
  mortgage_repaid_date: String
  will_be_repaid_by_mortgage_id: String
  repayment_amount: String
  interest_only_amount: String
  monthly_payment: String
  revert_monthly_payment: String
  mortgage_amount: String
  selected_mortgage_club_code: String
  requires_offset_facility: Boolean
  loan_completion_date: String
  loan_contract_exchange_date: String
  term: Int
  term_unit: TermUnit
  esis_document_id: String
  mortgage_product_select_reason: String
  first_or_second_charge: Int
  repayment_vehicle_current_value_of_vehicle: String
  repayment_vehicle_monthly_contribution: String
  repayment_vehicle_declared_maturity_amount: String
  application_date: String
  offer_expiry_date: String
  mortgage_product_source_rank: Int
  raw_results_reference: String
  source_response: SourceResponse
  calculated_values: MortgageCalculatedValues
  version: String
  lender_proposed: Boolean
  acre_dip_id: String
  provider_dip_reference: String
  provider_application_reference: String
  valuation_type: String
  dip_status: MortgageDipStatus
  dip_amount: String
  initial_rate_end_date: String
  fees_to_be_added: [MortgageProductFeeType]
  eor_document_ids: [String!]
  has_arrears: Boolean
  arrears_summary: MortgageArrears
  payment_history: [PaymentHistory]
  payment_status_summary: PaymentStatusSummary
  external: [ExternalMortgage]
  property_value: Int
  versions: [VersionWithChangedFields]
  reason_for_true_cost_calculation_change: String
  dip_details: MortgageDipDetails
  document_requirements: [MortgageDocumentRequirement!]
}

type MortgageDipDetails {
  manual_verification_at_fma: [String]
  dip_decision_reason: String
  dip_timestamp: String
  dip_detail: String
}

type MortgageDocumentRequirement {
  requirement_id: ID!
  source: MortgageDocumentRequirementSource
  source_id: String
  details: DocumentRequirementDetails
}

input MortgageDocumentRequirementInput {
  requirement_id: ID
  source: MortgageDocumentRequirementSource
  source_id: String
  details: DocumentRequirementDetailsInput
}

type CreatedMortgage {
  mortgage_id: ID
  status: String
  repayment_amount: Int
}

type MortgageProductResponse {
  apr_lenders: Int
  arrangement_fee: String
  booking_fee: String
  chaps_fee: String
  deeds_release_fee: String
  early_repayment_charge_applies_until: String
  early_repayment_charge_applies_until_calculated_date: String
  fees_total: String
  initial_monthly_payment: Float
  initial_pay_rate: Int
  standard_variable_rate: Int
  initial_rate_period: String
  initial_rate_period_months: Float
  legal_fee: String
  lender_code: String
  lender_name: String
  lender_product_reference: String
  max_loan_available: String
  max_ltv_available: Int
  mortgage_class: MortgageClass
  mortgage_discharge_fee: String
  net_proc_fee: Float
  product_availability: String
  product_code: ID
  product_match_status: String
  product_name: String
  product_type: String
  true_cost_over_calculated_period: String
  true_cost_over_initial_period: String
  true_cost_full_term: String
  packager_name: String
  cashback: String
  broker_fee: Float
  valuation_fee: String
}

input ExternalMortgageSourceInput {
  id: String
  name: ExternalIdNames
}

input ExternalMortgageInput {
  id: String
  source: ExternalMortgageSourceInput
}

input MortgageProductResponseInput {
  apr_lenders: Int
  arrangement_fee: String
  booking_fee: String
  chaps_fee: String
  deeds_release_fee: String
  early_repayment_charge_applies_until: String
  early_repayment_charge_applies_until_calculated_date: String
  fees_total: String
  initial_monthly_payment: Float
  initial_pay_rate: Int
  initial_rate_period: String
  initial_rate_period_months: Float
  legal_fee: String
  lender_code: String
  lender_name: String
  lender_reference: String
  lender_product_reference: String
  max_loan_available: String
  max_ltv_available: Int
  selected_mortgage_club_code: String
  mortgage_class: MortgageClass
  mortgage_discharge_fee: String
  net_proc_fee: Float
  product_availability: String
  product_code: String
  product_match_status: String
  product_name: String
  product_type: String
  standard_variable_rate: Int
  true_cost: String
  true_cost_full_term: String
  true_cost_over_calculated_period: String
  true_cost_over_initial_period: String
  packager_name: String
  cashback: String
  broker_fee: Float
  broker_fee_conditions: String
  higher_lending_charge: String
  valuation_fee: String
  disbursement_fee: String
  capital_rest_period: String
  applicant_type_conditions: String
  full_rate_description: String
  cashback_conditions: String
  funds_transfer_fee_rule: String
  self_build: String
  shared_ownership: String
  booking_fee_conditions: String
  higher_lending_charge_conditions: String
  product_notes: String
  employed_prob_period: String
  studio_flat_flag: String
  proc_fee_code: String
  proc_fee_percentage: Int
  valuation_fee_conditions: String
  legal_fee_conditions: String
  free_legal_fee_conditions: String
  applicant1_income_multiplier: Float
  applicant2_income_multiplier: Float
  joint_applicant_income_multiplier: Float
  fees_payable_on_application: String
  fees_payable_on_completion: String
  monthly_payment_after_initial_period_plus_1perc: Float
  monthly_payment_after_initial_period_plus_2perc: Float
  monthly_payment_after_initial_period_plus_3perc: Float
  max_property_valuation: String
  proc_fee_fixed: String
  proc_fee_min: String
  proc_fee_max: String
  free_legal_fees: Boolean
  help_to_buy_shared_equity: Boolean
  initial_monthly_payment_inc_existing_mortgage: Float
  true_cost_full_term_inc_existing_mortgage: Float
  true_cost_initial_period_inc_existing_mortgage: Float
  true_cost_inc_existing_mortgage: Float
  max_overpayment_percent_no_erc: Int
  overpayments_allowed: Boolean
  underpayments_allowed: Boolean
  payment_holidays_allowed: Boolean
  date_last_modified: String
  optional_partial_repayment: Boolean
  apply_online: Boolean
}

type SourceResponse {
  serialised_data: String
  products: [MortgageProduct!]
  generated_at: String
  generated_for: String
  signature: String
  source_details: SourceDetails
}

input SourceResponseInput {
  serialised_data: String
  generated_at: String
  generated_for: String
  signature: String
}

input MortgageAndProductInput {
  mortgage_id: ID
  product_id: ID
  product_code: String
  source_response: String
}

type MortgageAndProduct {
  mortgage_id: String
  product_id: String
}

type MortgageAndCase {
  mortgage_id: String
  case_id: String
}

type MutatePropertyAndMortgagesResponse {
  property: PropertyVersion
  createdMortgage: Mortgage
  updatedMortgages: [Mortgage!]
}

input MortgageAndCaseInput {
  mortgage_id: String
  case_id: String
}

type MortgageProductToCase {
  status: MortgageStatus
  case_id: String
  product_code: String
  repayment_amount: Int
  source_response: SourceResponse
}

input UpdateMortgagesInput {
  mortgageId: String
  mortgageProductId: String
  mortgageInput: MortgageInput
  mortgageProductInput: MortgageProductActualInput
}

input MutatePropertyAndMortgagesInput {
  propertyId: ID
  propertyInput: PropertyInput
  updateMortgageInput: [UpdateMortgagesInput!]
  createMortgageInput: CreateMortgageInput
}

input CreateMortgageInput {
  caseId: ID
  mortgageInput: MortgageInput!
  mortgageProductInput: MortgageProductActualInput!
}

input MortgageProductToCaseInput {
  clientIds: [String]
  assumes_continuing: [String!]
  status: MortgageStatus
  case_id: String
  product_code: String
  repayment_amount: String
  source_response: String
  term: Int
  term_unit: TermUnit
  mortgage_amount: String
  mortgage_type: MortgageType
  initial_monthly_payment: Float
  property_secured_ids: [String!]
  preference_target_property_value: String
  part_investment_loan_required_investment_part: String
  property_id: String
}

type MortgageIdAndMortgageProduct {
  id: ID!
  mortgage_product: MortgageProduct!
}

input RateTiersInput {
  rate: Int
  rate_type: String
  period_fixed_length_months: Int
  period_fixed_end_date: String
  rate_tracked: String
  discount_rate: Int
  until_end_of_term: Boolean
}

input MortgageProductInput {
  lender_name: String
  product_name: String
  fees_total: String
  standard_variable_rate: Int
  initial_pay_rate: Int
  initial_rate_period_months: Int
  initial_rate_period: String
  product_code: String
  lender_reference: String
  rate_tiers: [RateTiersInput!]
  is_porting: Boolean
  early_repayment_charge_applies: Boolean
  early_repayment_charge_periods: [ErcPeriodInput!]
  fees: [MortgageProductFeeInput]
  early_repayment_charge: String
  mortgage_club_proc_fees: [MortgageClubFeeInput!]
  mortgage_class: MortgageClass
  proc_fee_percentage: Int
  max_ltv_available: Int
  selected_mortgage_club_code: String
  net_proc_fee_percentage: Int
  product_type: String
  cashback: String
  cashback_paid_on: String
  proc_fee_fixed: String
  net_proc_fee_fixed: String
}

input MortgageInput {
  mortgage_id: ID
  assumes_continuing: [String!]
  property_secured_ids: [String!]
  mortgage_product_id: ID
  mortgage_product_source_rank: Int
  client_ids: [String]
  case_ids: [String]
  status: MortgageStatus
  lender_proposed: Boolean
  outstanding_balance: String
  outstanding_balance_date: String
  fees_to_be_added: [MortgageProductFeeType]
  current_monthly_payment: String
  early_repayment_charge_amount: String
  mortgage_end_date: String
  loan_benefits_all_applicants: Boolean
  additional_loan_amount: String
  additional_loan_purposes: [AdditionalLoanPurposes]
  additional_loan_reason_description: String
  mortgage_account_number: String
  mortgage_start_date: String
  mortgage_redemption_date: String
  property_purchase_date: String
  mortgage_repaid_date: String
  will_be_repaid_by_mortgage_id: String
  repayment_amount: String
  interest_only_amount: String
  requires_offset_facility: Boolean
  loan_completion_date: String
  loan_contract_exchange_date: String
  repayment_vehicle_provider_name: String
  repayment_vehicle_reference_number: String
  repayment_vehicle_current_value_of_vehicle: String
  repayment_vehicle_monthly_contribution: String
  repayment_vehicle_declared_maturity_amount: String
  repayment_vehicle_start_date: String
  repayment_vehicle_maturity_date: String
  repayment_vehicle_additional_information: String
  mortgage_product_select_reason: String
  mortgage_amount: String
  selected_mortgage_club_code: String
  monthly_payment: String
  revert_monthly_payment: String
  term: Int
  term_unit: TermUnit
  property_value: Int
  esis_document_id: String
  offer_letter_document_id: ID
  guarantor_client_ids: [String]
  first_or_second_charge: Int
  application_date: String
  offer_expiry_date: String
  acre_dip_id: String
  provider_dip_reference: String
  provider_application_reference: String
  valuation_type: String
  dip_status: MortgageDipStatus
  dip_amount: String
  version: String
  source_response: SourceResponseInput
  raw_results_reference: String
  has_arrears: Boolean
  arrears_summary: MortgageArrearsInput
  payment_status_summary: PaymentStatusSummary
  external: [ExternalMortgageInput]
}

input CreateMortgageWithProductInput {
  case_id: ID
  mortgage: MortgageInput
  product_details: MortgageProductInput
}

input UpdateMortgageWithProductInput {
  mortgage: MortgageInput
  product_details: MortgageProductInput
  mortgage_id: ID
}

type CreateMortgageWithProduct {
  case_id: ID
  mortgage_id: ID
  mortgage: Mortgage
  product_id: ID
  product_details: MortgageProduct
}

type MortgageWithProduct {
  mortgage_id: ID
  product_id: ID
  mortgage: Mortgage
  product_details: MortgageProduct
}

input MortgageArrearsInput {
  current_arrears_balance: String
  date_arrears_cleared: String
  max_arrears_in_forty_eight_months: String
  max_arrears_in_thirty_six_months: String
  max_arrears_in_twenty_four_months: String
  max_arrears_in_twelve_months: String
  max_arrears_in_six_months: String
  max_arrears_in_three_months: String
}

type MortgageArrears {
  current_arrears_balance: String
  date_arrears_cleared: String
  max_arrears_in_forty_eight_months: String
  max_arrears_in_thirty_six_months: String
  max_arrears_in_twenty_four_months: String
  max_arrears_in_twelve_months: String
  max_arrears_in_six_months: String
  max_arrears_in_three_months: String
}

# TODO FRON-1667 Removing unnecessary multiple Mortgage input
# types from schema above and then improve tha namings bellow :)
input MortgageProductActualInput {
  affordability_calculator: String
  applicant_maximum_age: Int
  applicant_type_conditions: String
  applicant1_income_multiplier: Float
  applicant1_minimum_age: Int
  applicant2_income_multiplier: Float
  applicant2_minimum_age: Int
  apply_online: Boolean
  apr_lenders: Int
  arrangement_fee_conditions: String
  arrangement_fee: String
  arrears_not_in_last_months: Int
  available_to_ftb: Boolean
  available_to_purchase: Boolean
  available_to_remortgage: Boolean
  booking_fee_conditions: String
  booking_fee: String
  broker_fee_conditions: String
  broker_fee: Float
  buy_to_let_rental_income_calculation_rate: Int
  capital_rest_period: String
  cashback_conditions: String
  cashback_paid_on: String
  cashback: String
  ccj_maximum_amount: Int
  ccj_maximum_number: Int
  chaps_fee: String
  commonhold_flag: String
  date_last_modified: String
  deeds_release_fee_when_to_pay: String
  deeds_release_fee: String
  disbursement_fee_conditions: String
  disbursement_fee: String
  early_repayment_charge_applies_until_calculated_date: String
  early_repayment_charge_applies_until: String
  early_repayment_charge_applies: Boolean
  early_repayment_charge_periods: [ErcPeriodInput!]
  early_repayment_charge: String
  employed_prob_period: String
  fees_payable_on_application: String
  fees_payable_on_completion: String
  fees_total: String
  feudal_flag: String
  flat_max_floors_no_lift: Int
  flat_max_floors: Int
  flat_over_shop_fast_food: String
  flat_over_shop_fast_industry_unit: String
  flat_over_shop_flag: String
  flat_over_shop_notes: String
  flat_over_shop_owner: String
  flat_over_shop_restaurant: String
  flat_over_shop_retail: String
  flat_over_shop_sep_ent: String
  flat_over_shop_sep_title: String
  flying_freehold_flag: String
  flying_freehold_max_percentage: Int
  free_legal_fee_conditions: String
  free_legal_fees: Boolean
  freehold_flag: String
  full_rate_description: String
  funds_transfer_fee_rule: String
  help_to_buy_mortgage_guarantee: Boolean
  help_to_buy_shared_equity: Boolean
  higher_lending_charge_conditions: String
  higher_lending_charge: String
  homebuyers_fee_conditions: String
  homebuyers_fee: String
  initial_monthly_payment_inc_existing_mortgage: Int
  initial_monthly_payment: Float
  initial_pay_rate: Int
  initial_rate_period_months: Int
  initial_rate_period: String
  is_further_advance: Boolean
  is_offset_product: Boolean
  is_porting: Boolean
  joint_applicant_income_multiplier: Float
  leasehold_flag: String
  legal_fee_conditions: String
  legal_fee: String
  lender_code: String
  lender_criteria_url: String
  lender_name: String
  lender_reference: String
  lender_product_reference: String
  lender_tel_no: String
  lender_web_address: String
  max_loan_available: String
  max_loan: String
  max_ltv_available: Int
  selected_mortgage_club_code: String
  max_overpayment_percent_no_erc: Int
  max_property_valuation: String
  min_loan: String
  min_self_employed_accounts_years: Int
  min_time_in_continuous_service_months: Int
  min_time_in_current_position_months: Int
  monthly_payment_after_initial_period_plus_1perc: Int
  monthly_payment_after_initial_period_plus_2perc: Int
  monthly_payment_after_initial_period_plus_3perc: Int
  mortgage_class: MortgageClass
  mortgage_club_proc_fees: [MortgageClubFeeInput!]
  mortgage_discharge_fee_when_to_pay: String
  mortgage_discharge_fee: String
  mortgage_type: MortgageProductDetailsMortgageType
  net_proc_fee_fixed: String
  net_proc_fee_max: String
  net_proc_fee_min: String
  net_proc_fee_percentage: Int
  net_proc_fee: Float
  optional_partial_repayment: Boolean
  overpayments_allowed: Boolean
  packager_name: String
  packager_tel_no: String
  packager_web_address: String
  payment_holidays_allowed: Boolean
  proc_fee_code: String
  proc_fee_fixed: String
  proc_fee_max: String
  proc_fee_min: String
  proc_fee_percentage: Int
  product_availability: String
  product_available_date: String
  product_code: String
  product_discontinued_date: String
  product_match_status: String
  product_name: String
  product_notes: String
  product_type: String
  rate_tiers: [RateTiersInput!]
  reject_reasons: String
  remort_bus_purpose_max_ltv: Int
  remort_debt_con_max_ltv: Int
  remort_hol_cars_max_ltv: Int
  remort_home_improv_max_ltv: Int
  repayment_acceptable: Boolean
  self_build: String
  self_years_avg_income: Int
  shared_ownership: String
  standard_variable_rate: Int
  stress_test_payments: [MortgageStressTestPaymentInput]
  studio_flat_flag: String
  true_cost_full_term_inc_existing_mortgage: Int
  true_cost_full_term: String
  true_cost_inc_existing_mortgage: Int
  true_cost_initial_period_inc_existing_mortgage: Int
  underpayments_allowed: Boolean
  valuation_fee_conditions: String
  valuation_fee: String
  fees: [MortgageProductFeeInput]
  incentives: [MortgageProductIncentiveInput]
}

input MortgageProductFeeInput {
  min_amount: String
  max_amount: String
  percentage: Int
  amount: String
  can_be_added_to_loan: CanBeAddedToLoan
  timing: MortgageProductFeeTiming
  refundable: Boolean
  fee_type: MortgageProductFeeType
  other_type: String
}

input MortgageProductIncentiveInput {
  amount: Int
  type: String
  maxAmount: Int
  method: String
}

input MortgageReasonInputForCase {
  id: ID
  mortgage_product_select_reason: String
  status: String
}
